/**
 * The map the boundary is drawn on: basemap, the project's orthophoto, the other boundaries as
 * outlines, the drawn polygons, the draft and the vertex handles. Structure follows `PlannerMap`;
 * all state changes are dispatched to the editor reducer (boundary-editor design §4.3).
 */
import { useEffect, useLayoutEffect, useRef } from 'react';
import type { Feature, FeatureCollection, Geometry } from 'geojson';
import { addProtocol, Map as MapLibreMap, NavigationControl, ScaleControl, type MapMouseEvent } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { Box, SegmentedControl } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { midpoints } from '@/features/missions/polygonEditor';
import { BASEMAP_SOURCE, BASEMAPS, RASTER_TILE_SIZE } from '@/map/layers';
import { serveCachedTile, TILE_CACHE_PROTOCOL } from '@/map/tileCache';
import type { LngLat } from '@/planner/types';
import { MAP } from '@/theme/colors';
import { brand } from '@/theme/theme';
import { ringAt, type Action, type EditorState, type RingRef } from './boundaryEditor';
import type { Ring } from './geometry';
import type { Problem } from './validate';

// The orthophoto's tiles come through the token-less tile cache, as on the report map (tileCache.ts).
addProtocol(TILE_CACHE_PROTOCOL, serveCachedTile);

export interface BoundaryMapProps {
  state: EditorState;
  dispatch: (a: Action) => void;
  basemap: 'osm' | 'ortho';
  onBasemapChange: (b: 'osm' | 'ortho') => void;
  /** The orthophoto's tile URL template, or null for none. */
  orthoTiles: string | null;
  /** MapLibre's transformRequest, adding the tiler token. */
  transformRequest?: (url: string, resourceType?: string) => { url: string } | undefined;
  /** The project's other boundaries, drawn as reference outlines. */
  outlines: FeatureCollection;
  problems: Problem[];
  /**
   * Where to look first, `[w, s, e, n]`; applied once, the first time it is non-null. After that
   * the view moves only when a replace (import, start from) brings new polygons.
   */
  initialBounds: [number, number, number, number] | null;
  /** Show the polygons only: a click selects, nothing else is dispatched, no handles or draft. */
  readOnly?: boolean;
}

const SRC = ['outlines', 'polygons', 'draft', 'vertices', 'midpoints'] as const;
type Src = (typeof SRC)[number];
const ORTHO_SOURCE = 'ortho';
const ORTHO_LAYER = 'ortho-raster';
const PICK_PX = 8;
const EMPTY: FeatureCollection = { type: 'FeatureCollection', features: [] };
const NO_DRAFT: Ring = [];

interface Handle {
  ref: RingRef;
  index: number;
  at: LngLat;
}

function fc(features: Feature<Geometry>[]): FeatureCollection {
  return { type: 'FeatureCollection', features };
}
function point(p: LngLat, props: Record<string, unknown> = {}): Feature<Geometry> {
  return { type: 'Feature', geometry: { type: 'Point', coordinates: p }, properties: props };
}
function closed(ring: Ring): LngLat[] {
  return [...ring, ring[0]];
}

/** The rings of the selected polygon, shown only while editing. */
function editableRings(s: EditorState): { ref: RingRef; ring: Ring }[] {
  if (s.mode !== 'edit' || s.selected === null || !s.polygons[s.selected]) return [];
  const polygon = s.selected;
  const refs: RingRef[] = [{ polygon, hole: -1 }, ...s.polygons[polygon].holes.map((_, hole) => ({ polygon, hole }))];
  return refs.map((ref) => ({ ref, ring: ringAt(s.polygons, ref) }));
}

function vertexHandles(s: EditorState): Handle[] {
  return editableRings(s).flatMap(({ ref, ring }) => ring.map((at, index) => ({ ref, index, at })));
}
function midpointHandles(s: EditorState): Handle[] {
  return editableRings(s).flatMap(({ ref, ring }) => midpoints(ring).map((at, index) => ({ ref, index, at })));
}

/** The state as the map shows it: a read-only map has no draft and no edit handles. */
function shown(p: BoundaryMapProps): EditorState {
  return p.readOnly ? { ...p.state, mode: 'view', draft: NO_DRAFT } : p.state;
}

function handleProps(h: Handle) {
  return { polygon: h.ref.polygon, hole: h.ref.hole, index: h.index };
}

export function BoundaryMap(props: BoundaryMapProps) {
  const { t } = useTranslation('boundary');
  const container = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MapLibreMap | null>(null);
  const propsRef = useRef(props);
  useLayoutEffect(() => {
    propsRef.current = props;
  });
  const dragging = useRef<{ ref: RingRef; index: number; started: boolean } | null>(null);
  const fitted = useRef(false);

  // One map for the component's life; data flows through source updates below.
  useEffect(() => {
    if (!container.current) return;
    const basemap = BASEMAPS[propsRef.current.basemap];
    const map = new MapLibreMap({
      container: container.current,
      style: {
        version: 8,
        sources: { [BASEMAP_SOURCE]: { type: 'raster', tiles: [basemap.url], tileSize: 256, maxzoom: 19, attribution: basemap.attribution } },
        layers: [{ id: 'lyr-basemap', type: 'raster', source: BASEMAP_SOURCE }],
      },
      center: [15.5, 49.8],
      zoom: 7,
      attributionControl: {},
      transformRequest: (url: string, resourceType?: string) => propsRef.current.transformRequest?.(url, resourceType),
    });
    mapRef.current = map;
    map.addControl(new NavigationControl({ showCompass: false }), 'top-left');
    map.addControl(new ScaleControl({ unit: 'metric' }), 'bottom-left');
    map.on('load', () => {
      for (const id of SRC) map.addSource(id, { type: 'geojson', data: EMPTY });
      map.addLayer({ id: 'outlines-line', type: 'line', source: 'outlines', paint: { 'line-color': MAP.marginLine, 'line-width': 1.5, 'line-dasharray': [3, 2] } });
      map.addLayer({ id: 'polygons-fill', type: 'fill', source: 'polygons', paint: { 'fill-color': ['case', ['get', 'problem'], MAP.problem, MAP.areaFill], 'fill-opacity': ['case', ['get', 'problem'], 0.15, 1] } });
      map.addLayer({
        id: 'polygons-line',
        type: 'line',
        source: 'polygons',
        paint: { 'line-color': ['case', ['get', 'problem'], MAP.problem, MAP.areaLine], 'line-width': ['case', ['get', 'selected'], 3, 2] },
      });
      map.addLayer({ id: 'draft-line', type: 'line', source: 'draft', filter: ['==', '$type', 'LineString'], paint: { 'line-color': MAP.areaLine, 'line-width': 2, 'line-dasharray': [3, 2] } });
      map.addLayer({ id: 'draft-circle', type: 'circle', source: 'draft', filter: ['==', '$type', 'Point'], paint: { 'circle-radius': 5, 'circle-color': MAP.vertex, 'circle-stroke-color': MAP.vertexOutline, 'circle-stroke-width': 2 } });
      map.addLayer({ id: 'midpoints-circle', type: 'circle', source: 'midpoints', paint: { 'circle-radius': 4, 'circle-color': MAP.midpoint, 'circle-stroke-color': MAP.vertexOutline, 'circle-stroke-width': 1 } });
      map.addLayer({ id: 'vertices-circle', type: 'circle', source: 'vertices', paint: { 'circle-radius': 6, 'circle-color': MAP.vertex, 'circle-stroke-color': MAP.vertexOutline, 'circle-stroke-width': 2 } });
      syncAll(map, propsRef.current);
      // A replace that landed before the map loaded could not fit then; it wins over the initial view.
      const replacedBounds = propsRef.current.state.replaced > 0 ? polygonBounds(propsRef.current.state.polygons) : null;
      if (replacedBounds) {
        fitted.current = true;
        fit(map, replacedBounds);
      } else fitOnce(map, propsRef.current.initialBounds, fitted);
    });

    const near = (a: { x: number; y: number }, b: LngLat) => {
      const q = map.project(b);
      return Math.hypot(q.x - a.x, q.y - a.y) < PICK_PX;
    };
    const nearest = (p: { x: number; y: number }, handles: Handle[]): Handle | null => {
      let best: Handle | null = null;
      let bestD = PICK_PX;
      for (const h of handles) {
        const q = map.project(h.at);
        const d = Math.hypot(q.x - p.x, q.y - p.y);
        if (d < bestD) {
          best = h;
          bestD = d;
        }
      }
      return best;
    };
    const selectAt = (p: MapMouseEvent['point']) => {
      const idx = map.queryRenderedFeatures(p, { layers: ['polygons-fill'] })[0]?.properties?.index;
      propsRef.current.dispatch({ type: 'select', polygon: typeof idx === 'number' ? idx : null });
    };

    map.on('click', (e: MapMouseEvent) => {
      const { state, dispatch, readOnly } = propsRef.current;
      if (readOnly) {
        selectAt(e.point);
        return;
      }
      const at: LngLat = [e.lngLat.lng, e.lngLat.lat];
      if (state.mode === 'draw' || state.mode === 'hole') {
        dispatch(state.draft.length >= 3 && near(e.point, state.draft[0]) ? { type: 'close' } : { type: 'click', at });
        return;
      }
      if (state.mode === 'edit') {
        if (nearest(e.point, vertexHandles(state))) return;
        const mid = nearest(e.point, midpointHandles(state));
        if (mid) {
          dispatch({ type: 'insert', ring: mid.ref, after: mid.index, at: mid.at });
          return;
        }
        const hit = map.queryRenderedFeatures(e.point, { layers: ['polygons-fill'] })[0]?.properties?.index;
        if (typeof hit === 'number') dispatch({ type: 'select', polygon: hit });
        return;
      }
      selectAt(e.point);
    });

    map.on('mousedown', (e: MapMouseEvent) => {
      const { state, readOnly } = propsRef.current;
      if (readOnly || state.mode !== 'edit' || e.originalEvent.button !== 0) return;
      const v = nearest(e.point, vertexHandles(state));
      if (!v) return;
      dragging.current = { ref: v.ref, index: v.index, started: false };
      map.dragPan.disable();
      e.preventDefault();
    });
    map.on('mousemove', (e: MapMouseEvent) => {
      const drag = dragging.current;
      if (!drag) return;
      // The undo step is taken at the first movement, so a plain click or a right-click leaves none.
      if (!drag.started) {
        drag.started = true;
        propsRef.current.dispatch({ type: 'dragStart' });
      }
      propsRef.current.dispatch({ type: 'move', ring: drag.ref, index: drag.index, at: [e.lngLat.lng, e.lngLat.lat] });
    });
    const endDrag = () => {
      if (!dragging.current) return;
      dragging.current = null;
      map.dragPan.enable();
    };
    map.on('mouseup', endDrag);
    // A release outside the map never reaches the map's own mouseup.
    window.addEventListener('mouseup', endDrag);
    map.on('contextmenu', (e: MapMouseEvent) => {
      const { state, dispatch, readOnly } = propsRef.current;
      if (readOnly || state.mode !== 'edit') return;
      const v = nearest(e.point, vertexHandles(state));
      if (!v) return;
      e.originalEvent.preventDefault();
      dispatch({ type: 'deleteVertex', ring: v.ref, index: v.index });
    });
    map.on('dblclick', (e: MapMouseEvent) => {
      const { state, dispatch, readOnly } = propsRef.current;
      if (readOnly || (state.mode !== 'draw' && state.mode !== 'hole')) return;
      // The two clicks of a double-click each appended a vertex at the same spot.
      const last = state.draft[state.draft.length - 1];
      const prev = state.draft[state.draft.length - 2];
      const dropLast = prev !== undefined && near(map.project(last), prev);
      e.preventDefault();
      dispatch({ type: 'close', dropLast });
    });

    return () => {
      window.removeEventListener('mouseup', endDrag);
      map.remove();
      mapRef.current = null;
    };
  }, []);

  const { problems, outlines, orthoTiles, initialBounds } = props;
  const state = shown(props);
  useEffect(() => {
    const map = loaded(mapRef.current);
    if (map) setData(map, 'outlines', outlines);
  }, [outlines]);
  useEffect(() => {
    const map = loaded(mapRef.current);
    if (map) setData(map, 'polygons', polygonsFc(state, problems));
  }, [state.polygons, state.selected, problems]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const map = loaded(mapRef.current);
    if (map) setData(map, 'draft', draftFc(state.draft));
  }, [state.draft]);
  useEffect(() => {
    const map = loaded(mapRef.current);
    if (map) syncHandles(map, state);
  }, [state.polygons, state.selected, state.mode]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const map = loaded(mapRef.current);
    if (map) syncOrtho(map, orthoTiles);
  }, [orthoTiles]);
  useEffect(() => {
    const map = loaded(mapRef.current);
    if (map) fitOnce(map, initialBounds, fitted);
  }, [initialBounds]);
  useEffect(() => {
    const map = loaded(mapRef.current);
    const bounds = polygonBounds(state.polygons);
    if (state.replaced === 0 || !map || !bounds) return;
    fitted.current = true;
    fit(map, bounds);
  }, [state.replaced]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    map.getCanvas().style.cursor = state.mode === 'view' ? '' : 'crosshair';
    if (state.mode === 'draw' || state.mode === 'hole') map.doubleClickZoom.disable();
    else map.doubleClickZoom.enable();
  }, [state.mode]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    try {
      if (!map.getSource(BASEMAP_SOURCE) || !map.getLayer('outlines-line')) return;
      const b = BASEMAPS[props.basemap];
      map.removeLayer('lyr-basemap');
      map.removeSource(BASEMAP_SOURCE);
      map.addSource(BASEMAP_SOURCE, { type: 'raster', tiles: [b.url], tileSize: 256, maxzoom: 19, attribution: b.attribution });
      // The basemap goes back under everything, the orthophoto included.
      map.addLayer({ id: 'lyr-basemap', type: 'raster', source: BASEMAP_SOURCE }, map.getLayer(ORTHO_LAYER) ? ORTHO_LAYER : 'outlines-line');
    } catch {
      // Style not loaded yet: the mount effect used the current basemap already.
    }
  }, [props.basemap]);

  return (
    <Box pos="relative" h="100%" style={{ borderRadius: 12, overflow: 'hidden', border: `1px solid ${brand.border}` }}>
      <div ref={container} style={{ position: 'absolute', inset: 0, background: MAP.background }} />
      <Box pos="absolute" top={8} right={8} bg={MAP.controlBg} p={6} style={{ borderRadius: 8, border: `1px solid ${MAP.controlBorder}` }}>
        <SegmentedControl
          size="xs"
          value={props.basemap}
          onChange={(v) => props.onBasemapChange(v as 'osm' | 'ortho')}
          data={[
            { value: 'osm', label: t('map.osm') },
            { value: 'ortho', label: t('map.ortho') },
          ]}
        />
      </Box>
    </Box>
  );
}

function setData(map: MapLibreMap, id: Src, data: FeatureCollection) {
  const src = map.getSource(id) as { setData: (d: FeatureCollection) => void } | undefined;
  src?.setData(data);
}

/** The map once its sources exist, else null. */
function loaded(map: MapLibreMap | null): MapLibreMap | null {
  return map && map.getSource('polygons') ? map : null;
}

function polygonsFc(s: EditorState, problems: Problem[]): FeatureCollection {
  const flagged = new Set(problems.map((p) => p.polygon));
  return fc(
    s.polygons.map((p, index) => ({
      type: 'Feature',
      geometry: { type: 'Polygon', coordinates: [closed(p.shell), ...p.holes.map(closed)] },
      properties: { index, selected: index === s.selected, problem: flagged.has(index) },
    })),
  );
}

/** The draft as a line, with the closing edge previewed once it could be closed, and its vertices. */
function draftFc(draft: Ring): FeatureCollection {
  if (!draft.length) return EMPTY;
  const line = draft.length >= 3 ? closed(draft) : draft;
  const features: Feature<Geometry>[] = draft.map((p) => point(p));
  if (line.length >= 2) features.push({ type: 'Feature', geometry: { type: 'LineString', coordinates: line }, properties: {} });
  return fc(features);
}

function syncHandles(map: MapLibreMap, s: EditorState) {
  setData(map, 'vertices', fc(vertexHandles(s).map((h) => point(h.at, handleProps(h)))));
  setData(map, 'midpoints', fc(midpointHandles(s).map((h) => point(h.at, handleProps(h)))));
}

function syncOrtho(map: MapLibreMap, tiles: string | null) {
  if (map.getSource(ORTHO_SOURCE)) {
    if (map.getLayer(ORTHO_LAYER)) map.removeLayer(ORTHO_LAYER);
    map.removeSource(ORTHO_SOURCE);
  }
  if (!tiles) return;
  map.addSource(ORTHO_SOURCE, { type: 'raster', tiles: [tiles], tileSize: RASTER_TILE_SIZE, maxzoom: 22 });
  map.addLayer({ id: ORTHO_LAYER, type: 'raster', source: ORTHO_SOURCE }, 'outlines-line');
}

function syncAll(map: MapLibreMap, p: BoundaryMapProps) {
  setData(map, 'outlines', p.outlines);
  const s = shown(p);
  setData(map, 'polygons', polygonsFc(s, p.problems));
  setData(map, 'draft', draftFc(s.draft));
  syncHandles(map, s);
  syncOrtho(map, p.orthoTiles);
}

type Bounds = [number, number, number, number];

function polygonBounds(ps: EditorState['polygons']): Bounds | null {
  const pts = ps.flatMap((p) => p.shell);
  if (!pts.length) return null;
  const xs = pts.map((p) => p[0]);
  const ys = pts.map((p) => p[1]);
  return [Math.min(...xs), Math.min(...ys), Math.max(...xs), Math.max(...ys)];
}

function fitOnce(map: MapLibreMap, bounds: Bounds | null, fitted: { current: boolean }) {
  if (!bounds || fitted.current) return;
  fitted.current = true;
  fit(map, bounds);
}

function fit(map: MapLibreMap, [w, s, e, n]: Bounds) {
  map.fitBounds(
    [
      [w, s],
      [e, n],
    ],
    { padding: 40, maxZoom: 18, duration: 0 },
  );
}
